import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const faqs = [
  {
    q: 'How much does window tinting cost in Palm Beach County?',
    a: 'Automotive window tinting typically ranges from $150 to $600+ depending on vehicle size and film type. A standard sedan costs around $200–$350, while larger SUVs may run $350–$600. Residential tinting is priced per square foot. Contact us for a free, no-obligation quote tailored to your specific needs.',
  },
  {
    q: 'How long does window tinting take?',
    a: 'Most automotive tinting jobs are completed in 2–4 hours and are ready the same day. Larger vehicles like SUVs or full-wrap PPF jobs may take a full day. Residential projects are scheduled based on square footage and access requirements. We work efficiently without compromising precision.',
  },
  {
    q: 'Is window tint legal in Florida?',
    a: 'Yes — Florida law allows front side windows to allow 28% or more light transmission, while rear side and back windows can be any darkness. Our technicians ensure every installation complies with Florida\'s window tint laws so you\'ll never have to worry about getting pulled over.',
  },
  {
    q: 'How long does window tint last?',
    a: 'With professional installation using premium 3M, LLumar, or XPEL films, window tint typically lasts 10–25 years. All our installations are backed by a lifetime warranty covering peeling, bubbling, fading, and discoloration. Cheap films from bargain shops may fail in as little as 2–3 years.',
  },
  {
    q: 'Can tinting be removed or changed?',
    a: 'Yes, window tint can be professionally removed and replaced at any time. We offer film removal services as well as upgrades to newer film technology. Attempting DIY removal can damage your defroster lines or glass — always have it done professionally.',
  },
  {
    q: 'What is the difference between regular tint and ceramic tint?',
    a: 'Regular dyed films block light primarily through color saturation. Ceramic films use nano-ceramic particles that reject infrared heat without relying on darkness — meaning you can have a lighter shade that still blocks significant heat. Ceramic tint is clearer, stronger, and more signal-friendly for GPS and mobile devices.',
  },
  {
    q: 'Do you offer mobile window tinting services?',
    a: 'We primarily operate from our Palm City facility for quality control purposes, but we do offer mobile services for large residential, commercial, and fleet projects. Contact us directly to discuss your project requirements and availability.',
  },
  {
    q: 'How should I care for my new window tint?',
    a: 'After installation, avoid rolling down windows for 3–5 days while the film cures. Do not clean the interior glass for 1–2 weeks. When cleaning, use a soft microfiber cloth and ammonia-free cleaner. Following these simple steps ensures your tint lasts decades.',
  },
];

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="faq" className="py-28 bg-[#0D0D0D] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(to right, #FF6B35 1px, transparent 1px)', backgroundSize: '64px 64px' }}
      />

      <div className="max-w-4xl mx-auto px-6 lg:px-12 relative z-10" ref={ref}>
        {/* Header */}
        <div className={`anim-hidden ${isVisible ? 'animate-fade-in-up' : ''} text-center mb-16`}>
          <span className="luxury-divider mx-auto" />
          <span className="text-xs font-bold uppercase tracking-[5px] text-[#C9A96E] mb-4 block">
            FAQ
          </span>
          <h2
            className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight mb-4"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            Frequently Asked
            <br />
            <span className="text-[#FF6B35]">Questions</span>
          </h2>
          <p className="text-white/50 text-sm max-w-xl mx-auto">
            Straight answers to the most common questions about window tinting, PPF, and ceramic coatings.
          </p>
        </div>

        {/* FAQ items */}
        <div className="space-y-3">
          {faqs.map((faq, i) => {
            const open = openIndex === i;
            return (
              <div
                key={i}
                className={`anim-hidden ${isVisible ? `animate-fade-in-up delay-${Math.min(i * 50, 400)}` : ''} rounded-2xl border overflow-hidden transition-all duration-300 cursor-pointer ${
                  open
                    ? 'bg-white/[0.06] border-[#FF6B35]/40'
                    : 'bg-white/[0.03] border-white/8 hover:border-white/15'
                }`}
                onClick={() => setOpenIndex(open ? null : i)}
              >
                <div className="flex items-start gap-4 p-6">
                  <span
                    className="text-[#FF6B35] text-xs font-bold tracking-widest mt-0.5 min-w-[24px]"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span
                    className="flex-1 text-sm font-semibold text-white leading-snug"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {faq.q}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-white/40 text-xl flex-shrink-0 transition-transform duration-300 ${
                      open ? 'rotate-180' : ''
                    }`}
                  />
                </div>
                {open && (
                  <div className="px-6 pb-6 pl-[calc(1.5rem+24px+1rem)]">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          className={`anim-hidden ${isVisible ? 'animate-fade-in-up delay-400' : ''} mt-14 text-center`}
        >
          <p className="text-white/40 text-sm mb-5">Still have questions?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+15612759473"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-white/5 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-[#FF6B35]" />
              Call (561) 275-9473
            </a>
            <a
              href="#quote"
              className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-7 py-3.5 rounded-full text-sm font-bold transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
